import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { services, industries } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const Quote = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    industry: '',
    location: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Get the service and industry titles from the data
      const serviceTitle = services.find(s => s.id === formData.service)?.title || formData.service;
      const industryTitle = industries.find(i => i.id === formData.industry)?.title || formData.industry || '';
      
      const response = await axios.post('/api/quote', {
        ...formData,
        service: serviceTitle,
        industry: industryTitle
      });
      
      if (response.data.success) {
        setSubmitted(true);
      } else {
        setError(response.data.message || 'Failed to submit quote request. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting quote:', err);
      setError('Failed to submit quote request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-50">
        <SEO 
          title="Quote Request Submitted - Thank You"
          description="Your security quote request has been received. Our team will contact you within 24 hours to discuss your security needs."
        />
        <div className="text-center px-4">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#1e3a52] mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Your quote request has been received. Our team will review your requirements and contact you within 24 hours.
          </p>
          <Link to="/">
            <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7]">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Request a Quote - Free Security Services Quote in Miami, Florida"
        description="Get a customized security solution quote for your business. Fill out our form and receive a free quote for armed guards, unarmed guards, mobile patrols, event security, and more in Miami, Florida."
        keywords="security quote, free security quote Miami, security services quote, get security quote, request security quote, security guard quote Florida"
      />
      {/* Hero Section */}
      <section className="relative h-[300px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&h=600&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
            <ChevronRight size={16} />
            <span className="text-[#5ba4c8]">Request a Quote</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Request a Quote
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl">
            Get a customized security solution for your needs
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-[#1e3a52] mb-6">Tell Us About Your Security Needs</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-[#1e3a52]">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#1e3a52]">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-[#1e3a52]">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#1e3a52]">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company" className="text-[#1e3a52]">Company Name</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#1e3a52]">Service Required *</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, service: value })}>
                    <SelectTrigger className="mt-1 border-gray-300">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1e3a52]">Industry</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                    <SelectTrigger className="mt-1 border-gray-300">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry.id} value={industry.id}>
                          {industry.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="location" className="text-[#1e3a52]">Location *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="City, State"
                  className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-[#1e3a52]">Additional Details</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us more about your security requirements..."
                  className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#5ba4c8] hover:bg-[#4a93b7] text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
